/**
 * RFC 4122 v4 UUID built on `crypto.getRandomValues`, which has been available far
 * longer than `crypto.randomUUID` (Chrome 11 vs Chrome 92) and so is safely inside
 * the Chrome 87 build target Vite defaults to.
 */
function randomUUID() {
    const bytes = new Uint8Array(16);
    crypto.getRandomValues(bytes);
    // RFC 4122 §4.4: version 4 in the high nibble of octet 6, variant 10xx in octet 8.
    bytes[6] = (bytes[6] & 0x0f) | 0x40;
    bytes[8] = (bytes[8] & 0x3f) | 0x80;
    let uuid = '';
    for (let i = 0; i < bytes.length; i++) {
        if (i === 4 || i === 6 || i === 8 || i === 10)
            uuid += '-';
        uuid += bytes[i].toString(16).padStart(2, '0');
    }
    return uuid;
}
/**
 * Install `crypto.randomUUID` where the browser does not provide a working one.
 *
 * react-hook-form's `generateId` (used by `useFieldArray` for row keys) calls
 * `crypto.randomUUID()` behind a truthiness check. Embedded Android WebViews have
 * been observed in production with the property present but *not callable*, which
 * satisfies that check and throws `TypeError: crypto.randomUUID is not a function`
 * during render — blanking the page, since nothing catches a throw from render.
 *
 * Hence the callability guard below: a `!crypto.randomUUID` check would not fire in
 * that case, because the injected value is truthy.
 */
export function installCryptoRandomUUID() {
    if (typeof crypto === 'undefined')
        return;
    if (typeof crypto.randomUUID === 'function')
        return;
    // Without getRandomValues there is nothing trustworthy to build a UUID on, and
    // installing a Math.random-backed one globally would mislead any future caller
    // reaching for crypto expecting cryptographic randomness.
    if (typeof crypto.getRandomValues !== 'function') {
        clearBrokenRandomUUID();
        return;
    }
    // Reflect returns false rather than throwing if the property cannot be redefined.
    const installed = Reflect.defineProperty(crypto, 'randomUUID', {
        value: randomUUID,
        writable: true,
        configurable: true,
        enumerable: false,
    });
    if (!installed)
        clearBrokenRandomUUID();
}
/**
 * Make the unusable `crypto.randomUUID` go away, so callers guarding on truthiness
 * take their own fallback path instead of throwing. This is what lets react-hook-form
 * reach the `Math.random` branch it already ships for old browsers.
 */
function clearBrokenRandomUUID() {
    if (Reflect.deleteProperty(crypto, 'randomUUID'))
        return;
    // Non-configurable but possibly still writable, in which case the value can be
    // replaced even though the property cannot be deleted. Note that defineProperty
    // is no use here: it cannot flip `configurable` back to true.
    if (Reflect.set(crypto, 'randomUUID', undefined))
        return;
    // Nothing further we can do to this object. Failing to patch it is bad, but
    // taking down app startup over it is worse.
    console.error('Could not clear the broken crypto.randomUUID');
}
